/* eslint-disable consistent-return */
/* eslint-disable array-callback-return */
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTrail, animated as a } from '@react-spring/web';

import AsteriskIcon from '../../icons/Asterisk';
import IntersectingVideo from './IntersectingVideo';

import * as AppPropTypes from '../../../lib/PropTypes';

import styles from '../../../../styles/blocks/employee/pills.module.scss';
import Detector from '../../partials/Detector';

const propTypes = {
    isContainer: PropTypes.bool,
    pills: AppPropTypes.pills,
    colors: PropTypes.shape({
        type: PropTypes.oneOf(['none', 'color', 'gradient']),
        colors: PropTypes.arrayOf(PropTypes.string),
    }),
    text: PropTypes.string,
    className: PropTypes.string,
};

const defaultProps = {
    isContainer: false,
    pills: null,
    colors: {
        type: 'none',
        colors: [],
    },
    text: null,
    className: null,
};

const Pills = ({ isContainer, pills, colors, text, className }) => {
    const [visible, setVisible] = useState(false);

    const { type: colorType = null, colors: customColors = [] } = colors || {};
    const isGradient = colorType === 'gradient';

    const colorStyles = useMemo(() => {
        if (colorType === 'color') return { color: customColors[0] };
        if (colorType === 'gradient')
            return {
                backgroundImage: `linear-gradient(to right,  ${customColors[0]}, ${customColors[1]}, ${customColors[0]})`,
            };
        return null;
    }, [colorType, customColors]);

    const [springs, setSprings] = useTrail(pills?.length, () => ({
        opacity: 0,
    }));

    useEffect(() => {
        if (visible) {
            setSprings.start({
                opacity: 1,
            });
        } else {
            setSprings.start({
                opacity: 0,
            });
        }
    }, [visible]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.animated]: isGradient,
                    [styles.isGradient]: isGradient,
                },
            ])}
        >
            <Detector rootMargin="-20%" onChange={(v) => setVisible(v)}>
                <div
                    className={classNames([
                        styles.inner,
                        {
                            [styles.isContainer]: isContainer,
                        },
                    ])}
                >
                    <div className={styles.line} style={colorStyles} />
                    <div className={styles.pills}>
                        {(springs || []).map((spring, i) => {
                            const {
                                type = null,
                                color = null,
                                text: pillText = null,
                                src = null,
                                width = null,
                                pills: subPills = null,
                            } = pills[i] || {};

                            if (type === 'text') {
                                return (
                                    <a.span
                                        key={`pill-${color} - ${i + 1}`}
                                        className={styles.textPill}
                                        style={{
                                            ...spring,
                                            color: `${color}`,
                                            borderColor: `${color}`,
                                        }}
                                    >
                                        {i === 0 ? (
                                            <AsteriskIcon className={styles.asterisk} />
                                        ) : null}
                                        {pillText}
                                    </a.span>
                                );
                            }
                            if (type === 'video') {
                                return (
                                    <a.div
                                        key={`pill-${color} - ${i + 1}`}
                                        className={styles.videoContainer}
                                        style={{
                                            ...spring,
                                        }}
                                    >
                                        <IntersectingVideo
                                            src={src}
                                            type="video/mp4"
                                            muted
                                            loop
                                            // preload="auto"
                                            className={styles.videoPill}
                                            style={{ width: `${width}px`, borderColor: `${color}` }}
                                        />
                                    </a.div>
                                );
                            }
                            if (type === 'block') {
                                return (
                                    <a.div
                                        key={`pill-block-${color} - ${i + 1}`}
                                        className={styles.blockContainer}
                                        style={{ ...spring }}
                                    >
                                        {(subPills || []).map(
                                            ({ text: subText = null, color: subColor = null }) => (
                                                <span
                                                    key={`pill-${subColor} - ${i + 1}`}
                                                    className={styles.textPill}
                                                    style={{
                                                        ...spring,
                                                        color: `${subColor}`,
                                                        borderColor: `${subColor}`,
                                                    }}
                                                >
                                                    {i === 0 ? (
                                                        <AsteriskIcon className={styles.asterisk} />
                                                    ) : null}
                                                    {subText}
                                                </span>
                                            ),
                                        )}
                                    </a.div>
                                );
                            }
                        })}
                    </div>
                    {text !== null ? (
                        <p
                            className={styles.text}
                            style={{ color: pills[0]?.color !== null ? pills[0]?.color : null }}
                        >
                            {text}
                        </p>
                    ) : null}
                </div>
            </Detector>
        </div>
    );
};

Pills.propTypes = propTypes;
Pills.defaultProps = defaultProps;

export default Pills;
